import { escapePanelAttribute, indentPanelBlock } from "../panel-markdown"

interface TurndownRule {
	filter: (node: Node) => boolean
	replacement: (content: string, node: Node) => string
}

interface TurndownLike {
	addRule: (key: string, rule: TurndownRule) => void
}

const typeOf = (node: Node): string | null =>
	node.nodeType === 1 ? (node as HTMLElement).getAttribute("data-type") : null

const hasAttribute = (node: Node, name: string): boolean =>
	node.nodeType === 1 && (node as HTMLElement).hasAttribute(name)

// Tiles sit in Mintlify's Columns; a single full-width tile is written on its own.
export function addTileMarkdownRules(turndown: TurndownLike): void {
	turndown.addRule("mintlifyTileGroup", {
		filter: (node) => node.nodeName === "DIV" && typeOf(node) === "tile-group",
		replacement: (content, node) => {
			const element = node as HTMLElement
			const cols = element.getAttribute("data-cols") ?? "3"
			const tiles = Array.from(element.childNodes).filter(
				(child) => typeOf(child) === "tile",
			).length
			if (cols === "1" && tiles === 1) return `\n\n${content.trim()}\n\n`
			return `\n\n<Columns cols={${cols}}>\n${indentPanelBlock(content.trim())}\n</Columns>\n\n`
		},
	})

	turndown.addRule("mintlifyTile", {
		filter: (node) => node.nodeName === "DIV" && typeOf(node) === "tile",
		replacement: (content, node) => {
			const element = node as HTMLElement
			const title = element.querySelector("[data-tile-title]")
			const description =
				element.querySelector("[data-tile-description]")?.textContent?.trim() ??
				""
			const href = title?.querySelector("a")?.getAttribute("href")
			const props = [
				href ? `href="${escapePanelAttribute(href)}"` : "",
				`title="${escapePanelAttribute(title?.textContent?.trim() ?? "")}"`,
				description ? `description="${escapePanelAttribute(description)}"` : "",
			]
				.filter(Boolean)
				.join(" ")
			const body = content.trim()
			return body
				? `\n<Tile ${props}>\n${indentPanelBlock(body)}\n</Tile>\n`
				: `\n<Tile ${props} />\n`
		},
	})

	turndown.addRule("mintlifyTilePreview", {
		filter: (node) =>
			node.nodeName === "DIV" && hasAttribute(node, "data-tile-preview"),
		replacement: (content) => `\n\n${content.trim()}\n\n`,
	})

	turndown.addRule("mintlifyTileLines", {
		filter: (node) =>
			node.nodeName === "P" &&
			(hasAttribute(node, "data-tile-title") ||
				hasAttribute(node, "data-tile-description")),
		replacement: () => "",
	})
}
